import React, { useContext, useEffect, useState } from 'react';

import FeedItemList from './FeedItemList';
import FeedsTabs from './FeedsTabs';
import PostcodePrompt from './PostcodePrompt';

import { trackGAEvent, trackGAEventOnce } from '../../../../utils/gaTracking';
import getFeedItems, { ALBATROSS_FEED_CACHE } from '../../repositories/getFeedItems';
import saveActiveFeed from '../../repositories/saveActiveFeed';
import MpCardBanner from '../Marketing/MpCardBanner';
import convertSnakeToPascalCase from './utils/convertSnakeToPascalCase';

import EnvironmentContext from '../../../../contexts/EnvironmentContext';
import LoginDialog from '../../components/LoginDialog/LoginDialog';
import { TFeed, TFeedItem, TFeeds } from 'homepage-web-bff/types/TFeeds';
import { TMpCard } from 'homepage-web-bff/types/TMpCard';

type TFeedsProps = {
  feeds: TFeeds;
  feedPostcode?: string;
  initialFeed?: number;
  favoriteAdIds?: string[];
  mpCards?: TMpCard[];
};

/**
 * Postcode prompt is shown if feed requires location but user postcode is not known
 */
const shouldShowPostcodePrompt = (feed: TFeed, feedPostcode?: string): boolean =>
  feed?.locationRequired && !feedPostcode;

const SEEN_LOGIN_GATE = 'SeenLoginGate';

const wasLoginDialogShown = () => typeof window !== 'undefined' && localStorage.getItem(SEEN_LOGIN_GATE) === 'true';

const Feeds = ({ feeds, feedPostcode, initialFeed = 0, favoriteAdIds, mpCards = [] }: TFeedsProps) => {
  const [activeFeedIndex, setActiveFeedIndex] = useState(initialFeed);
  const [feedsPages, setFeedsPages] = useState({});
  const [items, setItems] = useState<TFeedItem[]>([]);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showLoginDialog, setShowLoginDialog] = useState<boolean>(false);
  const {
    userLoggedIn,
    isBot,
    hasConsentBannerBeenDismissed,
    experiments,
    tenantContext: { tenant },
    xsrfToken,
  } = useContext(EnvironmentContext);

  const { isMpCardBannerEnabled, isAlbatrossRecommendationsEnabled } = experiments;
  const isMp = (tenant ?? '') === 'mp';

  const fetchFeedItems = async (feed: TFeed, page: number): Promise<void> => {
    setIsLoading(true);
    // Group B of the Albatross experiment gets its own short-lived "For You" cache instead of the 6 hour one.
    // Marktplaats only: other tenants (2dehands) are in the labs group too, but are served the regular feed.
    const experimentCache =
      isMp && Boolean(isAlbatrossRecommendationsEnabled) && feed.feedType === 'FOR_YOU'
        ? ALBATROSS_FEED_CACHE
        : undefined;
    const feedItems = await getFeedItems(
      { feed, feedPostcode, page, favoriteAdIds, experimentCache },
      userLoggedIn,
      xsrfToken,
    );
    if (feedItems?.length) {
      setItems((prevItems) => [...prevItems, ...feedItems]);
      setHasNextPage(true);
    } else {
      // having no items back means feed-api is either broken or has no data for feed
      // anyway infinite scroll should be disabled in this case
      setHasNextPage(false);
    }

    if (page === 0) {
      const eventLabel = convertSnakeToPascalCase(feed.feedType);
      if (feedItems.length) {
        trackGAEvent('Displayed', eventLabel);
        trackGAEventOnce('FirstLoadNotEmpty', eventLabel);
      } else {
        trackGAEvent('EmptyStateDisplayed', eventLabel);
      }
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (!feeds.length) {
      return;
    }

    const pages =
      feeds.length &&
      feeds.reduce(
        (acc, feed) => ({
          ...acc,
          [feed.feedType]: 0,
        }),
        {},
      );
    const feed = feeds[activeFeedIndex];

    setFeedsPages(pages);
    setActiveFeedIndex(activeFeedIndex);
    saveActiveFeed(activeFeedIndex);

    const eventLabel = convertSnakeToPascalCase(feed.feedType);
    trackGAEvent('TabSelected', eventLabel);

    const page = pages[feed.feedType];
    if (page === 0) {
      trackGAEvent(`LoadMore${page}`, eventLabel);
    }

    // try to restore state from cache or fetch from server
    void fetchFeedItems(feed, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isBot && !userLoggedIn && hasConsentBannerBeenDismissed && !wasLoginDialogShown()) {
      setShowLoginDialog(true);
      localStorage.setItem(SEEN_LOGIN_GATE, 'true');
    }
  }, [userLoggedIn, isBot, hasConsentBannerBeenDismissed]);

  const handleLoadMore = (pageIncrement = 1) => {
    const feed = feeds[activeFeedIndex];
    const page = feedsPages[feed.feedType] + pageIncrement;

    setFeedsPages((prevPages) => ({
      ...prevPages,
      [feed.feedType]: page,
    }));

    trackGAEvent(`LoadMore${page}`, convertSnakeToPascalCase(feed.feedType));
    // fetch new items
    void fetchFeedItems(feed, page);
  };

  const handleFeedChange = (index: number) => {
    setActiveFeedIndex(index);
    saveActiveFeed(index);
    // reset items when switching between tabs
    setItems([]);
    setHasNextPage(true);

    const feed = feeds[index];
    const page = feedsPages[feed.feedType];
    const eventLabel = convertSnakeToPascalCase(feed.feedType);
    trackGAEvent('TabSelected', eventLabel);

    if (!shouldShowPostcodePrompt(feeds[index], feedPostcode)) {
      if (page === 0) {
        trackGAEvent(`LoadMore${page}`, eventLabel);
      }
      // try to restore state from cache or fetch from server
      void fetchFeedItems(feed, page);
    }
  };

  if (!feeds.length) {
    return null;
  }

  return (
    <>
      {isMpCardBannerEnabled && mpCards.length > 0 && <MpCardBanner mpCards={mpCards} />}
      <FeedsTabs feeds={feeds} activeFeedIndex={activeFeedIndex} onClick={handleFeedChange} />
      {shouldShowPostcodePrompt(feeds[activeFeedIndex], feedPostcode) ? (
        <PostcodePrompt />
      ) : (
        <FeedItemList
          activeFeedIndex={activeFeedIndex}
          items={items}
          hasNextPage={hasNextPage}
          isLoading={isLoading}
          onLoadMore={handleLoadMore}
          feedType={feeds[activeFeedIndex].feedType}
          mpCards={mpCards}
          setShowLoginDialog={setShowLoginDialog}
          {...(feeds[activeFeedIndex].locationRequired ? { feedPostCode: feedPostcode } : {})}
          isMp={isMp}
        />
      )}
      {showLoginDialog && <LoginDialog onClose={setShowLoginDialog} />}
    </>
  );
};

export default Feeds;
